/**
 * WorkstreamGraph — SVG visualization of the Architect Agent LangGraph
 * workflow (intake → classify → decompose → skill_match → routing →
 * plan_emit → validation_gate → dispatch → monitor).
 *
 * Option A from Phase 6: pure SVG, ~150 LOC, no React Flow dependency.
 * Monochrome chrome per ADR-057 — only the node state badge is colored
 * (status taxonomy: pending=gray, running=blue, done=green, failed=red,
 * skipped=yellow).
 *
 * Layout: left-to-right Sankey-style flow with the canonical 9 nodes
 * arranged in a single row; the SVG width fills the container and each
 * node card is fixed-width. Connections are simple horizontal lines.
 */
import type { ArchitectNode, ArchitectNodeState } from '@chaos/types/architect';
import * as React from 'react';

interface WorkstreamGraphProps {
  nodes: ArchitectNode[];
  selectedNodeId: string | null;
  onSelect: (nodeId: string) => void;
}

// Card geometry — tweak in one place.
const CARD_WIDTH = 124;
const CARD_HEIGHT = 64;
const CARD_GAP = 24;
const PADDING_X = 16;
const PADDING_Y = 20;

/**
 * Status-bound chip color. Pulled from the existing `--color-*` tokens
 * so light/dark themes flip automatically.
 */
function nodeStateChip(state: ArchitectNodeState): {
  label: string;
  bg: string;
  fg: string;
} {
  switch (state) {
    case 'done':
      return {
        label: 'done',
        bg: 'hsl(var(--color-success) / 0.18)',
        fg: 'hsl(var(--color-success))',
      };
    case 'running':
      return {
        label: 'running',
        bg: 'hsl(var(--status-blue-bg))',
        fg: 'hsl(var(--status-blue-fg))',
      };
    case 'failed':
      return {
        label: 'failed',
        bg: 'hsl(var(--color-error) / 0.18)',
        fg: 'hsl(var(--color-error))',
      };
    case 'skipped':
      return {
        label: 'skipped',
        bg: 'hsl(var(--color-warning) / 0.18)',
        fg: 'hsl(var(--color-warning))',
      };
    default:
      return {
        label: 'pending',
        bg: 'hsl(var(--color-muted))',
        fg: 'hsl(var(--color-muted-foreground))',
      };
  }
}

function formatLabel(name: string): string {
  // intake → Intake, skill_match → Skill match, plan_emit → Plan emit
  return name
    .split('_')
    .map((piece, i) => (i === 0 ? piece.charAt(0).toUpperCase() + piece.slice(1) : piece))
    .join(' ');
}

export function WorkstreamGraph({
  nodes,
  selectedNodeId,
  onSelect,
}: WorkstreamGraphProps): React.JSX.Element {
  const sorted = React.useMemo(() => [...nodes].sort((a, b) => a.position - b.position), [nodes]);

  const innerWidth = sorted.length * CARD_WIDTH + Math.max(0, sorted.length - 1) * CARD_GAP;
  const svgWidth = innerWidth + PADDING_X * 2;
  const svgHeight = CARD_HEIGHT + PADDING_Y * 2;

  return (
    <div className="relative inline-block overflow-x-auto">
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        width={svgWidth}
        height={svgHeight}
        role="img"
        aria-label="Architect workflow graph"
        className="block"
      >
        {/* Connector lines */}
        {sorted.slice(0, -1).map((node, i) => {
          const x1 = PADDING_X + (i + 1) * CARD_WIDTH + i * CARD_GAP;
          const x2 = x1 + CARD_GAP;
          const y = PADDING_Y + CARD_HEIGHT / 2;
          return (
            <line
              key={`edge-${node.id}`}
              x1={x1}
              y1={y}
              x2={x2}
              y2={y}
              stroke="hsl(var(--color-border))"
              strokeWidth={1}
            />
          );
        })}
        {/* Node cards — visual representation */}
        {sorted.map((node, i) => {
          const x = PADDING_X + i * (CARD_WIDTH + CARD_GAP);
          const y = PADDING_Y;
          const chip = nodeStateChip(node.state);
          const isSelected = node.id === selectedNodeId;
          return (
            <g key={node.id} className="transition-opacity duration-150" pointerEvents="none">
              <rect
                x={x}
                y={y}
                width={CARD_WIDTH}
                height={CARD_HEIGHT}
                rx={4}
                ry={4}
                fill="hsl(var(--color-background))"
                stroke={isSelected ? 'hsl(var(--color-foreground))' : 'hsl(var(--color-border))'}
                strokeWidth={isSelected ? 1.5 : 1}
              />
              <text
                x={x + 12}
                y={y + 22}
                fontSize={12}
                fontWeight={500}
                fill="hsl(var(--color-foreground))"
                fontFamily="inherit"
              >
                {formatLabel(node.name)}
              </text>
              {/* Status chip */}
              <rect
                x={x + 12}
                y={y + 32}
                width={Math.max(40, chip.label.length * 6.4 + 12)}
                height={18}
                rx={3}
                ry={3}
                fill={chip.bg}
              />
              <text
                x={x + 18}
                y={y + 45}
                fontSize={10}
                fontWeight={500}
                fill={chip.fg}
                fontFamily="inherit"
              >
                {chip.label}
              </text>
            </g>
          );
        })}
      </svg>
      {/* HTML <button> overlays for accessibility — proper semantics
          for keyboard + screen-readers. Positioned absolutely over the
          SVG card rectangles. */}
      <div className="pointer-events-none absolute inset-0">
        {sorted.map((node, i) => {
          const x = PADDING_X + i * (CARD_WIDTH + CARD_GAP);
          const y = PADDING_Y;
          const chip = nodeStateChip(node.state);
          const isSelected = node.id === selectedNodeId;
          return (
            <button
              key={`hit-${node.id}`}
              type="button"
              aria-pressed={isSelected}
              aria-label={`${formatLabel(node.name)} (${chip.label})`}
              onClick={() => onSelect(node.id)}
              className="pointer-events-auto absolute cursor-pointer rounded bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40"
              style={{ left: x, top: y, width: CARD_WIDTH, height: CARD_HEIGHT }}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Pure helper exported for unit tests — given a list of nodes, returns
 * the canonical ordering used to lay out the graph.
 */
export function _orderNodesForGraph(nodes: ArchitectNode[]): ArchitectNode[] {
  return [...nodes].sort((a, b) => a.position - b.position);
}
